import React from 'react';
import environment from 'platform/utilities/environment';
import commonDefinitions from 'vets-json-schema/dist/definitions.json';
import FormFooter from 'platform/forms/components/FormFooter';
import { defaultItemPageScrollAndFocusTarget as scrollAndFocusTarget } from 'platform/forms-system/src/js/patterns/array-builder';
import { VA_FORM_IDS } from 'platform/forms/constants';
import { TITLE, SUBTITLE } from '../utils/constants';
import manifest from '../manifest.json';
import IntroductionPage from '../containers/IntroductionPage';
import ConfirmationPage from '../containers/ConfirmationPage';
import FormSavedPage from '../containers/FormSavedPage';
import { transform } from './submit-transformer';
import claimantRelationship from './chapters/01-applicant-information/claimantRelationship';
import claimantInformation from './chapters/01-applicant-information/claimantInformation';
import contactInformation from './chapters/01-applicant-information/contactInformation';
import mailingAddress from './chapters/01-applicant-information/mailingAddress';
import veteranInformation from './chapters/01-applicant-information/veteranInformation';
import firstTimeReporting from './chapters/02-expenses/firstTimeReporting';
import effectiveDates from './chapters/02-expenses/effectiveDates';
import reportingPeriod from './chapters/02-expenses/reportingPeriod';
import { careExpensesPages } from './chapters/02-expenses/careExpensesPages';
import { medicalExpensesPages } from './chapters/02-expenses/medicalExpensesPage';
import { mileageExpensesPages } from './chapters/02-expenses/mileageExpensesPage';
import supportingDocuments from './chapters/03-additional-information/supportingDocuments';
import uploadDocuments from './chapters/03-additional-information/uploadDocuments';
import expensesReview from './chapters/02-expenses/expensesReview';
import GetFormHelp from '../components/GetFormHelp';
import { hasNoExpenses, hasCareExpenses } from './chapters/02-expenses/helpers';
import { applyForceDivWrapperToFormConfig } from '../utils/uiSchemaHelpers';
import identificationInformation from './chapters/01-applicant-information/identificationInformation';
import content from '../utils/content.json';

/** @type {FormConfig} */
const formConfig = {
  rootUrl: manifest.rootUrl,
  urlPrefix: '/',
  submitUrl: `${environment.API_URL}/medical_expense_reports/v0/form8416`,
  transformForSubmit: transform,
  trackingPrefix: 'med-expense-8416',
  useCustomScrollAndFocus: true,
  v3SegmentedProgressBar: true,
  dev: {
    // disableWindowUnloadInCI: true,
    showNavLinks: !window.Cypress,
    collapsibleNavLinks: true,
  },
  // downtime: {
  //   dependencies: [externalServices.icmhs],
  // },
  formId: VA_FORM_IDS.FORM_21P_8416,
  // useCustomScrollAndFocus: false,
  defaultDefinitions: commonDefinitions,
  prefillEnabled: true,
  saveInProgress: {
    messages: {
      inProgress: content['config-sip-in-progress'],
      expired: content['config-sip-in-expired'],
      saved: content['config-sip-saved'],
    },
  },
  version: 0,
  formSavedPage: FormSavedPage,
  savedFormMessages: {
    notFound: content['config-saved-not-found'],
    noAuth: content['config-saved-no-auth'],
  },
  preSubmitInfo: {
    statementOfTruth: {
      body: (
        <div>
          <p>
            I confirm that the identifying information in this form is accurate
            and has been represented correctly.
          </p>
          <p>
            <span className="vads-u-font-weight--bold">
              I have not and will not
            </span>{' '}
            receive reimbursement for these expenses. I certify the information
            contained on this form and the attached addendums is a true
            representation of expenses I have paid.
          </p>
        </div>
      ),
      messageAriaDescribedby: content['config-pre-submit-aria'],
      fullNamePath: 'claimantFullName',
    },
  },
  title: TITLE,
  subTitle: SUBTITLE,
  introduction: IntroductionPage,
  confirmation: ConfirmationPage,
  footerContent: FormFooter,
  getHelp: GetFormHelp,
  showReviewErrors: !environment.isProduction() && !environment.isStaging(),
  chapters: {
    yourIdentity: {
      title: 'Your identity',
      pages: {
        claimantRelationship: {
          title: content['relationship-title'],
          path: 'applicant',
          uiSchema: claimantRelationship.uiSchema,
          schema: claimantRelationship.schema,
          scrollAndFocusTarget,
        },
      },
    },
    applicantInformation: {
      title: ({ formData }) =>
        formData?.claimantNotVeteran
          ? 'Personal and Veteran information'
          : 'Your information',
      pages: {
        claimantInformation: {
          title: content['claimant-name-title'],
          path: 'applicant/information',
          uiSchema: claimantInformation.uiSchema,
          schema: claimantInformation.schema,
        },
        mailingAddress: {
          title: content['mailing-address-title'],
          path: 'applicant/mail-address',
          uiSchema: mailingAddress.uiSchema,
          schema: mailingAddress.schema,
        },
        contactInformation: {
          title: content['contact-title'],
          path: 'applicant/contact',
          uiSchema: contactInformation.uiSchema,
          schema: contactInformation.schema,
        },
        veteranInformation: {
          title: content['veteran-name-title'],
          path: 'applicant/veteran-information',
          uiSchema: veteranInformation.uiSchema,
          schema: veteranInformation.schema,
          depends: formData => formData?.claimantNotVeteran,
        },
        identificationInformation: {
          title: formData =>
            formData?.claimantNotVeteran === false
              ? content['identification-title-veteran']
              : content['identification-title-claimant'],
          path: 'applicant/identification-information',
          uiSchema: identificationInformation.uiSchema,
          schema: identificationInformation.schema,
        },
      },
    },
    expenses: {
      title: 'Expenses',
      pages: {
        firstTimeReporting: {
          title: content['first-time-title'],
          path: 'expenses',
          uiSchema: firstTimeReporting.uiSchema,
          schema: firstTimeReporting.schema,
        },
        effectiveDates: {
          title: content['effective-dates-title'],
          path: 'expenses/effective-dates',
          depends: formData => formData?.firstTimeReporting === true,
          uiSchema: effectiveDates.uiSchema,
          schema: effectiveDates.schema,
        },
        reportingPeriod: {
          title: content['reporting-period-title'],
          path: 'expenses/reporting-period-dates',
          depends: formData => formData?.firstTimeReporting === false,
          uiSchema: reportingPeriod.uiSchema,
          schema: reportingPeriod.schema,
        },
        ...careExpensesPages,
        ...medicalExpensesPages,
        ...mileageExpensesPages,
        expensesReview: {
          title: 'Review expenses',
          path: 'expenses/review',
          depends: formData => hasNoExpenses(formData),
          uiSchema: expensesReview.uiSchema,
          schema: expensesReview.schema,
        },
      },
    },
    additionalInformation: {
      title: 'Additional information',
      pages: {
        supportingDocuments: {
          title: content['supporting-documents-title'],
          path: 'supporting-documents',
          depends: formData => hasCareExpenses(formData),
          uiSchema: supportingDocuments.uiSchema,
          schema: supportingDocuments.schema,
        },
        uploadDocuments: {
          title: 'Upload documents',
          path: 'upload-documents',
          uiSchema: uploadDocuments.uiSchema,
          schema: uploadDocuments.schema,
        },
      },
    },
  },
};

export default applyForceDivWrapperToFormConfig(formConfig);
